/**
 * Hash-based router (OMON-202) — gives the app a real, working browser
 * back/forward button, refresh-safe deep links, and bookmarkable URLs.
 *
 * THE CONTRACT: navigation state lives in the URL hash. Every nav action sets
 * `location.hash` (via `go`), and a single `hashchange` listener applies the
 * resulting route to the app (`applyRoute`). Back/forward fire the same
 * `hashchange`, so there is ONE code path for all navigation — click, restore,
 * and history traversal alike. `applyRoute` never sets the hash, so there is no
 * feedback loop.
 *
 * Routes:
 *   #/dashboard                         (the landing; also the empty hash)
 *   #/clients                           clients list
 *   #/clients/<eocId>/<tab>             a client's plan detail + tab
 *                                       (tab ∈ developmental | tasks | narrative)
 *   #/catalogue   #/connect             workflow panels
 *   #/admin/users-roles  #/admin/audit-log
 *
 * The plan-builder (create/edit draft) is deliberately NOT routed — it's a
 * transient editing flow with its own save/discard exits; pressing Back from it
 * lands on the client's detail route, which exits the builder cleanly.
 *
 * Auth/async note: a deep link can only be restored AFTER sign-in resolves and
 * the patient list has loaded, so the initial `applyRoute` runs at the end of
 * the post-login data load (see main.ts loadData), not on first paint.
 */

import { state } from "./state";
import { switchToTab, activateNav } from "./navigation";
import { selectPatient } from "./clients/list";
import { renderClientsView } from "./clients/view";
import { switchToAdminPanel } from "./admin/shell";
import { ensurePatients, isPatientListLoaded } from "./loaders/ensure";
import { currentUserListsClientsBroadly, loadPatientRow } from "./loaders/patients";
import type { PatientInfo } from "./types";
import { bootstrapProfile } from "./connect/profile";
import { renderConnectTimeline } from "./connect/timeline-view";
import { type Route, parseHash, buildHash } from "./router-parse";
import { routeRefusal } from "./route-guard";

export type { Route };
export { parseHash, buildHash };

// ── navigate (the ONLY way the app changes view) ─────────────────────────────

/**
 * Set the URL hash for a route. This pushes a browser-history entry and fires
 * `hashchange`, which applies the route. If the hash is unchanged (e.g. a
 * repeat click on the current tab) no event fires and nothing happens, which is
 * the desired no-op.
 */
export function go(route: Route): void {
  location.hash = buildHash(route);
}

// ── apply (state mutation + render; never sets the hash) ─────────────────────

/**
 * The patient a client / connect-client route names.
 *
 * From the list when it has been read this session (the row object the list
 * holds, so updates to it show in both places). Otherwise — a refreshed deep
 * link, the list never loaded — OMON-351 reads that ONE EpisodeOfCare instead
 * of every client's. `loadPatientRow` applies the list's own inclusion rule
 * (template / retired / wrong status → null), so an id the list would not
 * show still falls back to the list/overview exactly as before, as does one
 * the user cannot read.
 *
 * ☠️ Only for tokens that list clients BROADLY. A scoped token's list is cut
 * to its own care teams / managed organisations by the QUERY, and the
 * permissive rulesets (production, training, demo, test) would let a single
 * get read an EoC outside that scope — so a scoped deep link still resolves
 * against the scoped list, and can never open a client the list would hide.
 */
async function resolveRoutePatient(eocId: string): Promise<PatientInfo | null> {
  if (!isPatientListLoaded() && await currentUserListsClientsBroadly()) {
    return loadPatientRow(eocId);
  }
  await ensurePatients();
  return state.patients.find((p) => p.docId === eocId) ?? null;
}

export async function applyRoute(route: Route): Promise<void> {
  // Refuse before anything activates or loads (route-guard.ts). A refused
  // route navigates to the landing, whose own hashchange re-enters here and
  // is always allowed, so this cannot loop. Claims are loaded before the
  // first applyRoute (auth.ts → onSignedIn → main.ts loadData), so an
  // allowed deep link is never bounced for want of claims.
  const refusal = routeRefusal(route, state.authClaims);
  if (refusal !== null) {
    go(refusal);
    return;
  }

  if (route.kind === "panel") {
    await switchToTab(route.panel);
    return;
  }

  if (route.kind === "admin") {
    await switchToAdminPanel(route.panel);
    return;
  }

  if (route.kind === "connect-client") {
    // OMON-253: refresh-safe Connect deep link — restore the client's sensor
    // timeline in place instead of falling back to the overview list. Mirrors
    // the clients-panel client-detail route (activateNav, not switchToTab, so
    // there's no list-reset side effect).
    activateNav("connect");
    const patient = await resolveRoutePatient(route.eocId);
    if (!patient) {
      // Unknown / not-yet-loaded id → the overview rather than a blank client.
      await switchToTab("connect");
      return;
    }
    await bootstrapProfile();
    await renderConnectTimeline(route.eocId, patient.name);
    return;
  }

  // route.kind === "client" — show the clients panel and the patient's detail.
  activateNav("clients");
  // OMON-281: this route resolves an eocId to a patient. Boot no longer
  // pre-loads anything for every route, so the dependency is awaited here —
  // which is what preserves OMON-202's refresh-safe deep link. OMON-351: that
  // resolution no longer reads the whole list (see resolveRoutePatient).
  if (state.selectedPatient?.docId === route.eocId) {
    // Already loaded — just switch the tab (cheap; no plan reload). Also covers
    // returning from another panel where selectedPatient survived. renderClientsView
    // dispatches to renderPatientDetail for a selected patient in view mode.
    state.builderMode = "view";
    state.detailActiveTab = route.tab;
    renderClientsView();
    return;
  }
  const patient = await resolveRoutePatient(route.eocId);
  if (patient) {
    await selectPatient(patient, route.tab);
  } else {
    // Unknown / not-yet-loaded id → fall back to the list rather than a blank.
    await switchToTab("clients");
  }
}

// ── init ─────────────────────────────────────────────────────────────────────

/** Register the single hashchange listener. Call once at startup (main.ts). */
export function initRouter(): void {
  window.addEventListener("hashchange", () => {
    applyRoute(parseHash(location.hash)).catch((err) =>
      console.error("applyRoute failed:", err),
    );
  });
}

/** The route encoded in the current URL hash. */
export function currentRoute(): Route {
  return parseHash(location.hash);
}
